import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CONTACT_CHANNELS, type ContactChannel } from '@nemo/core/model';
import { useTRPC } from '../trpc.js';
import { Select } from '../ui/Select.js';
import { today } from '../ui/format.js';
import { useToast } from '../ui/Toast.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

export interface LogContactProps {
  fundId: string;
  fundName: string;
  /** The people already on file here, which is most of what gets logged. */
  people: readonly { id: string; name: string }[];
  onClose: () => void;
}

/** The picker's own value for "somebody who is not on this list yet". */
const SOMEBODY_ELSE = '';

const CHANNEL_LABELS: Record<ContactChannel, string> = {
  meeting: 'Meeting',
  call: 'Call',
  email: 'Email',
  event: 'Event',
  intro: 'Intro',
  other: 'Other',
};

/**
 * Somebody saying what they themselves did: they spoke to a person at this
 * fund, on a day, and here is what they learnt. The signed-in person is the
 * reporter — this is not a form for filing somebody else's conversation.
 *
 * The person is picked from the fund's own people, because that is nearly
 * always who it is, and typed where it is not; a typed name that cannot be
 * matched goes to the review queue rather than creating anybody.
 */
export function LogContact(props: LogContactProps): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [picked, setPicked] = useState(props.people[0]?.id ?? SOMEBODY_ELSE);
  const [typedName, setTypedName] = useState('');
  const [happenedAt, setHappenedAt] = useState(today());
  const [channel, setChannel] = useState<ContactChannel>('meeting');
  const [intel, setIntel] = useState('');

  const report = useMutation(
    trpc.contacts.report.mutationOptions({
      onSuccess: (filed) => {
        void queryClient.invalidateQueries({ queryKey: trpc.contacts.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.investors.list.queryKey() });
        invalidateFundRows(queryClient, trpc);
        void queryClient.invalidateQueries({ queryKey: trpc.enrichment.status.queryKey() });
        toast(
          filed.status === 'matched'
            ? `Logged against ${filed.investorName ?? ''}.`
            : 'Logged, but nobody of that name is on file here — it is waiting for review in Enrichment.',
        );
        props.onClose();
      },
      onError: (error: { message: string }) => toast(error.message),
    }),
  );

  const personName =
    picked === SOMEBODY_ELSE ? typedName.trim() : (nameOf(props.people, picked) ?? '');

  return (
    <form
      className="log-contact"
      onSubmit={(event) => {
        event.preventDefault();
        if (personName === '') return;
        report.mutate({
          personName,
          fundName: props.fundName,
          happenedAt,
          channel,
          intel: intel.trim() === '' ? null : intel.trim(),
        });
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        props.onClose();
      }}
    >
      <div className="log-contact-row">
        <Select
          label={`Who at ${props.fundName} you spoke to`}
          className="log-contact-person"
          value={picked}
          onChange={setPicked}
          options={[
            ...props.people.map((person) => ({ value: person.id, label: person.name })),
            { value: SOMEBODY_ELSE, label: 'Somebody else…' },
          ]}
          placeholder="Who you spoke to"
        />
        <input
          type="date"
          aria-label="The day it happened"
          value={happenedAt}
          onChange={(event) => setHappenedAt(event.target.value)}
        />
        <Select
          label="How it happened"
          className="log-contact-channel"
          value={channel}
          onChange={(value) => setChannel(value as ContactChannel)}
          options={CONTACT_CHANNELS.map((option) => ({
            value: option,
            label: CHANNEL_LABELS[option],
          }))}
        />
      </div>

      {picked === SOMEBODY_ELSE && (
        <input
          autoFocus
          aria-label="Their name"
          placeholder="Their name"
          value={typedName}
          onChange={(event) => setTypedName(event.target.value)}
        />
      )}

      <textarea
        className="pane-notes"
        rows={3}
        aria-label="What you learnt"
        placeholder="What you learnt"
        value={intel}
        onChange={(event) => setIntel(event.target.value)}
      />

      <div className="log-contact-row">
        <button className="button small primary" type="submit" disabled={report.isPending}>
          Log it
        </button>
        <button className="button small" type="button" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function nameOf(people: readonly { id: string; name: string }[], id: string): string | undefined {
  return people.find((person) => person.id === id)?.name;
}
